// ---------------------------------------------------------
// Datei: SignatureDialog.tsx
// Zweck: Mobile Eingabe einer handschriftlichen Signatur als PNG.
// Kontext: Serviceprotokolle benötigen getrennte Kunden- und Mitarbeiter-Signaturen.
// Stand: 2026-08-08
// ---------------------------------------------------------
import { useEffect, useRef } from 'react';
import SignaturePad from 'signature_pad';

interface SignatureDialogProps {
  isOpen: boolean;
  title: string;
  initialSignature?: string;
  onCancel: () => void;
  onSave: (dataUrl: string) => void;
}

const SignatureDialog: React.FC<SignatureDialogProps> = ({
  isOpen,
  title,
  initialSignature,
  onCancel,
  onSave,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const signaturePadRef = useRef<SignaturePad | null>(null);

  useEffect(() => {
    if (!isOpen) return undefined;

    const animationFrame = window.requestAnimationFrame(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const rect = canvas.getBoundingClientRect();
      const ratio = Math.max(window.devicePixelRatio || 1, 1);
      canvas.width = Math.max(Math.floor(rect.width * ratio), 1);
      canvas.height = Math.max(Math.floor(rect.height * ratio), 1);
      const context = canvas.getContext('2d');
      context?.scale(ratio, ratio);

      const pad = new SignaturePad(canvas, { penColor: '#111827', minWidth: 0.7, maxWidth: 2.2 });
      if (initialSignature) pad.fromDataURL(initialSignature);
      signaturePadRef.current = pad;
    });

    return () => {
      window.cancelAnimationFrame(animationFrame);
      signaturePadRef.current = null;
    };
  }, [initialSignature, isOpen]);

  if (!isOpen) return null;

  const clear = (): void => signaturePadRef.current?.clear();
  const save = (): void => {
    const pad = signaturePadRef.current;
    if (!pad || pad.isEmpty()) return;
    onSave(pad.toDataURL('image/png'));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end bg-black/50 sm:items-center sm:justify-center" role="dialog" aria-modal="true" aria-label={title}>
      <div className="w-full rounded-t-xl bg-white p-4 shadow-xl sm:max-w-xl sm:rounded-xl dark:bg-gray-900">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{title}</h3>
        <p className="mt-1 text-sm text-gray-600 dark:text-gray-300">Bitte im Feld unterschreiben.</p>
        <canvas
          ref={canvasRef}
          aria-label={`${title} zeichnen`}
          className="mt-4 h-48 w-full touch-none rounded-lg border border-gray-300 bg-white dark:border-gray-600"
        />
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          <button type="button" onClick={clear} className="rounded border px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            Löschen
          </button>
          <button type="button" onClick={onCancel} className="rounded border px-3 py-2 text-sm font-semibold text-gray-700 dark:text-gray-200">
            Abbrechen
          </button>
          <button type="button" onClick={save} className="rounded bg-primary-600 px-3 py-2 text-sm font-semibold text-white">
            Unterschrift übernehmen
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureDialog;
